import React, { useMemo } from 'react'
import { useState, useEffect, useRef } from 'react'
import { Search, Calendar, Tag, ExternalLink, Filter, ChevronDown, ChevronRight, Youtube, Globe, Clock, Eye, Star, MoreHorizontal, FolderInput, Check, StickyNote, Image as ImageIcon, Trash2 } from 'lucide-react'
import { useAuthStore } from '../stores/authStore'
import { motion, AnimatePresence } from 'framer-motion'
import { useSearchParams, useNavigate } from 'react-router-dom'
import RelatedArticles from '../components/RelatedArticles'
import { ToastContainer } from '../components/Toast'
import { ChatWidget } from '../components/ChatWidget'
import ContentCard from '../components/ContentCard'
import AssetViewerModal from '../components/AssetViewerModal'
import NoteEditorModal, { type NoteEditorAsset } from '../components/NoteEditorModal'
import FloatingActionButtons from '../components/FloatingActionButtons'
import ConfirmationModal from '../components/ConfirmationModal'
import SmartNotificationBanner from '../components/SmartNotificationBanner'
import DiscoverModal from '../components/DiscoverModal'

interface ContentItem {
  id: string
  url: string
  title: string
  content?: string
  summary: string
  takeaways: string[]
  tags: string[]
  category: string
  difficulty: string
  word_count: number
  createdAt: string
  related_to_content_id?: string | null
  metadata?: any
}

export default function LibraryPage() {
  const navigate = useNavigate()
  const [searchQuery, setSearchQuery] = useState('')
  const [contentItems, setContentItems] = useState<ContentItem[]>([])
  const [isLoading, setIsLoading] = useState(false) // Start false to prevent flash
  const [initialLoadDone, setInitialLoadDone] = useState(false) // Track if we've loaded once
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set())
  const { token, user } = useAuthStore()
  const [relatedContent, setRelatedContent] = useState<ContentItem[]>([])
  const [currentItem, setCurrentItem] = useState<ContentItem | null>(null)
  const [searchParams] = useSearchParams()
  const highlightId = searchParams.get('highlight') || searchParams.get('content') // Support both 'highlight' and 'content' params
  const spaceId = searchParams.get('space')
  const highlightHandledRef = useRef<string | null>(null) // Track handled highlights
  const fetchingRef = useRef(false) // Prevent double fetching
  
  // Track if we're processing a highlight navigation - prevents grid flash
  const [isHighlightNavigation, setIsHighlightNavigation] = useState(() => {
    // Initialize to true if we have a highlight param - prevents initial grid render
    const params = new URLSearchParams(window.location.search);
    return !!(params.get('highlight') || params.get('content'));
  });
  
  // Pagination state
  const [totalCount, setTotalCount] = useState(0)
  const [hasMore, setHasMore] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const ITEMS_PER_PAGE = 50
  
  // Modern UI state
  const [selectedCategory, setSelectedCategory] = useState('all')
  const [selectedSource, setSelectedSource] = useState('all')
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set(['technology', 'business']))
  const [sortBy, setSortBy] = useState<'newest' | 'oldest' | 'title' | 'category'>('newest')
  
  // Selection and organization state
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set())
  const [showMoveToSpace, setShowMoveToSpace] = useState(false)
  const [selectionMode, setSelectionMode] = useState(false)
  const [spaces, setSpaces] = useState<any[]>([])
  const [loadingSpaces, setLoadingSpaces] = useState(false)
  const [currentSpace, setCurrentSpace] = useState<any>(null)
  const [toasts, setToasts] = useState<any[]>([])
  
  // Asset viewer modal state
  const [assetViewerOpen, setAssetViewerOpen] = useState(false)
  const [selectedAsset, setSelectedAsset] = useState<{
    id: string
    title: string
    type: 'note' | 'screenshot'
    content?: string
    imageUrl?: string
    sourceTitle?: string
    sourceUrl?: string
    createdAt?: string
    metadata?: any
  } | null>(null)

  // Fabric-like note editor state
  const [noteEditorOpen, setNoteEditorOpen] = useState(false)
  const [selectedNote, setSelectedNote] = useState<NoteEditorAsset | null>(null)

  // Discover modal state
  const [showDiscoverModal, setShowDiscoverModal] = useState(false)

  // Continue Learning Banner state
  const [smartNotification, setSmartNotification] = useState<any>(null)
  
  // Learning paths for content items (for badges)
  const [contentLearningPaths, setContentLearningPaths] = useState<Record<string, any[]>>({})

  // User stats for greeting (streak + trending)
  const [userStats, setUserStats] = useState<{ streak: number } | null>(null)

  const getDismissedNotifications = (): Set<string> => {
    try {
      const dismissed = sessionStorage.getItem('Memtrail_dismissed_notifications')
      return dismissed ? new Set(JSON.parse(dismissed)) : new Set()
    } catch {
      return new Set()
    }
  }

  const markNotificationDismissed = (notificationKey: string) => {
    try {
      const dismissed = getDismissedNotifications()
      dismissed.add(notificationKey)
      sessionStorage.setItem('Memtrail_dismissed_notifications', JSON.stringify([...dismissed]))
    } catch (error) {
      console.error('Error saving dismissed notification:', error)
    }
  }

  const isNotificationDismissed = (notificationKey: string): boolean => {
    return getDismissedNotifications().has(notificationKey)
  }

  const addToast = (toast: any) => {
    const id = Date.now().toString()
    setToasts(prev => [...prev, { ...toast, id }])
    
    // Auto remove after duration
    setTimeout(() => {
      removeToast(id)
    }, toast.duration || 5000)
  }

  const removeToast = (id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id))
  }

  const toggleExpand = (id: string) => {
    const newExpandedItems = new Set(expandedItems);
    if (newExpandedItems.has(id)) {
      newExpandedItems.delete(id);
    } else {
      newExpandedItems.add(id);
    }
    setExpandedItems(newExpandedItems);
    setCurrentItem(contentItems.find(item => item.id === id) || null);
  }


  const toggleSelection = (id: string) => {
    const newSelected = new Set(selectedItems)
    if (newSelected.has(id)) {
      newSelected.delete(id)
    } else {
      newSelected.add(id)
    }
    setSelectedItems(newSelected)
  }

  const selectAll = () => {
    setSelectedItems(new Set(contentItems.map(item => item.id)))
  }

  const deselectAll = () => {
    setSelectedItems(new Set())
  }

  const toggleSelectionMode = () => {
    setSelectionMode(!selectionMode)
    if (selectionMode) {
      // Exiting selection mode, clear selections
      deselectAll()
    }
  };

  const isExpanded = (id: string) => expandedItems.has(id);

  // Modern UI helper functions

  const getCategoryColor = (category: string) => {
    const colors = {
      technology: 'bg-blue-50 text-blue-700 border-blue-200',
      business: 'bg-green-50 text-green-700 border-green-200',
      science: 'bg-purple-50 text-purple-700 border-purple-200',
      education: 'bg-orange-50 text-orange-700 border-orange-200',
      health: 'bg-red-50 text-red-700 border-red-200',
      entertainment: 'bg-pink-50 text-pink-700 border-pink-200',
      other: 'bg-gray-50 text-gray-700 border-gray-200'
    }
    return colors[category as keyof typeof colors] || colors.other
  }

  const getContentType = (url: string) => {
    if (!url) return 'web'
    if (url.includes('youtube.com') || url.includes('youtu.be')) {
      return 'youtube'
    }
    return 'web'
  }

  const getSourceIcon = (url: string) => {
    const contentType = getContentType(url || '')
    return contentType === 'youtube' ? Youtube : Globe
  }

  // Detect if item is a note or screenshot
  const isNote = (item: ContentItem) => {
    return item.metadata?.type === 'note' || 
           item.tags?.includes('note') || 
           item.title?.startsWith('Note:')
  }

  const isScreenshot = (item: ContentItem) => {
    return item.metadata?.type === 'screenshot' || 
           item.tags?.includes('screenshot') ||
           item.title?.includes('Screenshot')
  }

  // Get readable item type
  const getItemType = (item: ContentItem) => {
    if (isNote(item)) return 'note'
    if (isScreenshot(item)) return 'screenshot'
    if (getContentType(item.url || '') === 'youtube') return 'youtube'
    return 'web'
  }

  // Get type badge info
  const getTypeBadge = (item: ContentItem) => {
    const type = getItemType(item)
    switch (type) {
      case 'note':
        return { icon: StickyNote, label: 'Note', bg: 'bg-yellow-100', text: 'text-yellow-700', border: 'border-yellow-200' }
      case 'screenshot':
        return { icon: ImageIcon, label: 'Screenshot', bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-200' }
      case 'youtube':
        return { icon: Youtube, label: 'YouTube', bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-200' }
      default:
        return { icon: Globe, label: 'Web', bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-200' }
    }
  }

  // Helper function to capitalize first letter of a string
  const capitalizeFirstLetter = (str: string) => {
    if (!str) return str;
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  // Format date nicely - with safe fallback
  const formatDate = (dateStr?: string) => {
    if (!dateStr) return ''
    
    try {
      const date = new Date(dateStr)
      if (isNaN(date.getTime())) return ''
      
      const now = new Date()
      const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24))
      
      if (diffDays === 0) {
        return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
      } else if (diffDays === 1) {
        return 'Yesterday'
      } else if (diffDays < 7) {
        return date.toLocaleDateString('en-US', { weekday: 'short' })
      } else {
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
      }
    } catch {
      return ''
    }
  }
  
  // Safe full date formatter - returns empty string if no valid date
  const formatFullDate = (dateStr?: string) => {
    if (!dateStr) return ''
    
    try {
      const date = new Date(dateStr)
      if (isNaN(date.getTime())) return ''
      
      return date.toLocaleDateString('en-US', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    } catch {
      return ''
    }
  }

  const getThumbnail = (item: ContentItem) => {
    // Check metadata for thumbnail
    if (item.metadata?.thumbnail) {
      return item.metadata.thumbnail
    }
    // Extract YouTube thumbnail from URL
    if (item.url && getContentType(item.url) === 'youtube') {
      const videoId = extractYouTubeId(item.url)
      if (videoId) {
        return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`
      }
    }
    return null
  }

  const extractYouTubeId = (url: string) => {
    if (!url) return null
    const match = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/)
    return match ? match[1] : null
  }

  const formatDuration = (duration: string | number) => {
    if (!duration) return ''
    
    // If duration is a number (seconds), convert directly
    if (typeof duration === 'number') {
      const hours = Math.floor(duration / 3600)
      const minutes = Math.floor((duration % 3600) / 60)
      const seconds = duration % 60
      
      if (hours > 0) {
        return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
      }
      return `${minutes}:${seconds.toString().padStart(2, '0')}`
    }
    
    // If duration is a string (ISO 8601 format), parse it
    const match = duration.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/)
    if (!match) return duration
    
    const hours = parseInt(match[1] || '0')
    const minutes = parseInt(match[2] || '0')
    const seconds = parseInt(match[3] || '0')
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
    }
    return `${minutes}:${seconds.toString().padStart(2, '0')}`
  }

  const formatViewCount = (viewCount: string) => {
    const count = parseInt(viewCount)
    if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`
    if (count >= 1000) return `${(count / 1000).toFixed(1)}K`
    return count.toString()
  }

  // Get categories with counts
  const getCategories = () => {
    const categoryMap = new Map()
    contentItems.forEach(item => {
      // Exclude asset categories (screenshot, note)
      const isAsset = item.tags?.includes('note') || 
                     item.tags?.includes('screenshot') || 
                     item.metadata?.type === 'note' || 
                     item.metadata?.type === 'screenshot' ||
                     item.category === 'screenshot'
      
      if (isAsset) return
      
      const category = (item.category || 'other').toLowerCase() // Normalize to lowercase
      categoryMap.set(category, (categoryMap.get(category) || 0) + 1)
    })
    
    return Array.from(categoryMap.entries()).map(([name, count]) => ({
      name,
      count,
      color: getCategoryColor(name)
    })).sort((a, b) => b.count - a.count)
  }

  // Filter and sort content
  const getFilteredContent = () => {
    let filtered = contentItems.filter(item => {
      // Exclude notes and screenshots from main library view
      const isAsset = item.tags?.includes('note') || 
                     item.tags?.includes('screenshot') || 
                     item.metadata?.type === 'note' || 
                     item.metadata?.type === 'screenshot' ||
                     item.category === 'screenshot'
      
      if (isAsset) return false
      
      const title = item.title || ''
      const summary = item.summary || ''
      const tags = item.tags || []
      
      const matchesSearch = title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           summary.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           tags.some(tag => tag?.toLowerCase().includes(searchQuery.toLowerCase()))
      
      const matchesCategory = selectedCategory === 'all' || (item.category || 'other').toLowerCase() === selectedCategory
      const contentType = getContentType(item.url || '')
      const matchesSource = selectedSource === 'all' || contentType === selectedSource
      
      return matchesSearch && matchesCategory && matchesSource
    })

    // Sort content
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime()
        case 'oldest':
          return new Date(a.createdAt || 0).getTime() - new Date(b.createdAt || 0).getTime()
        case 'title':
          return (a.title || '').localeCompare(b.title || '')
        case 'category':
          return (a.category || 'other').localeCompare(b.category || 'other')
        default:
          return 0
      }
    })

    return filtered
  }

  // Group content by category
  const getGroupedContent = () => {
    const filtered = getFilteredContent()
    const grouped = new Map()
    
    filtered.forEach(item => {
      const category = (item.category || 'other').toLowerCase() // Normalize to lowercase
      if (!grouped.has(category)) {
        grouped.set(category, [])
      }
      grouped.get(category).push(item)
    })
    
    return Array.from(grouped.entries()).map(([category, items]) => ({
      category,
      items,
      count: items.length,
      color: getCategoryColor(category)
    })).sort((a, b) => b.count - a.count)
  }

  const toggleCategoryExpansion = (category: string) => {
    const newExpanded = new Set(expandedCategories)
    if (newExpanded.has(category)) {
      newExpanded.delete(category)
    } else {
      newExpanded.add(category)
    }
    setExpandedCategories(newExpanded)
  }

  // Content Modal Component for detailed view
  // Memoized to prevent re-creation when parent re-renders (e.g., when opening/closing asset viewer)
  // The key prop on usage ensures proper state reset when item changes
  const ContentModal = useMemo(() => {
    return function ContentModalInner({ item, onClose }: { item: ContentItem, onClose: () => void }) {
    const SourceIcon = getSourceIcon(item.url || '')
    const contentType = getContentType(item.url || '')
    const thumbnail = getThumbnail(item)
    const [activeTab, setActiveTab] = useState<'connect' | 'chat'>('connect')
    const [chatMessages, setChatMessages] = useState<Array<{role: 'user' | 'assistant', content: string}>>([])
    const [chatInput, setChatInput] = useState('')
    const [isSendingChat, setIsSendingChat] = useState(false)
    const chatMessagesEndRef = useRef<HTMLDivElement>(null)
    
    // Local edit mode state (inside modal for proper isolation)
    const [localEditMode, setLocalEditMode] = useState(false)
    const [localSummary, setLocalSummary] = useState(item.summary)
    const [localTakeaways, setLocalTakeaways] = useState([...item.takeaways])
    const [isSaving, setIsSaving] = useState(false)
    
    // Reset local state when item changes
    useEffect(() => {
      setLocalEditMode(false)
      setLocalSummary(item.summary)
      setLocalTakeaways([...item.takeaways])
    }, [item.id])
    
    // Save handler
    const handleSave = async () => {
      if (!token) return
      setIsSaving(true)
      try {
        const response = await fetch(`/api/content/${item.id}`, {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            summary: localSummary,
            takeaways: localTakeaways.filter(t => t.trim())
          })
        })
        if (response.ok) {
          // Update parent state
          const updatedItem = { ...item, summary: localSummary, takeaways: localTakeaways.filter(t => t.trim()) }
          setCurrentItem(updatedItem)
          setContentItems(prev => prev.map(i => i.id === item.id ? updatedItem : i))
          addToast({ type: 'success', message: 'Saved!', duration: 2000 })
          setLocalEditMode(false)
        } else {
          throw new Error('Failed to save')
        }
      } catch (error) {
        addToast({ type: 'error', message: 'Failed to save', duration: 3000 })
      } finally {
        setIsSaving(false)
      }
    }
    
    // Connect tab state
    const [relatedContent, setRelatedContentModal] = useState<ContentItem[]>([])
    const [connectionEntities, setConnectionEntities] = useState<Array<{name: string, type: string}>>([])
    const [relatedLoading, setRelatedLoading] = useState(false) // Start false - show empty state immediately
    const [knowledgeGaps, setKnowledgeGaps] = useState<Array<{topic: string, type: string}>>([])
    const [gapsLoading, setGapsLoading] = useState(true) // Start true - show shimmer while AI thinks
    
    // Resizable panel state
    const [panelWidth, setPanelWidth] = useState(400)
    const [isMaximized, setIsMaximized] = useState(false)
    const [isDragging, setIsDragging] = useState(false)
    const minPanelWidth = 300
    const maxPanelWidth = 700

    // Auto-scroll to bottom when new messages arrive
    useEffect(() => {
      if (chatMessagesEndRef.current) {
        chatMessagesEndRef.current.scrollIntoView({ behavior: 'smooth' })
      }
    }, [chatMessages, isSendingChat])
    
    // Handle resize drag
    const handleMouseDown = (e: React.MouseEvent) => {
      e.preventDefault()
      setIsDragging(true)
    }
    
    useEffect(() => {
      const handleMouseMove = (e: MouseEvent) => {
        if (!isDragging) return
        const containerWidth = window.innerWidth * 0.95 // max-w-[95vw]
        const newWidth = containerWidth - e.clientX + 32 // 32px for padding
        setPanelWidth(Math.min(maxPanelWidth, Math.max(minPanelWidth, newWidth)))
      }
      
      const handleMouseUp = () => {
        setIsDragging(false)
      }
      
      if (isDragging) {
        document.addEventListener('mousemove', handleMouseMove)
        document.addEventListener('mouseup', handleMouseUp)
        document.body.style.cursor = 'col-resize'
        document.body.style.userSelect = 'none'
      }
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
        document.body.style.cursor = ''
        document.body.style.userSelect = ''
      }
    }, [isDragging])

    // Assets linked to this content (Hybrid option C)
    const [linkedAssets, setLinkedAssets] = useState<ContentItem[]>([])
    const [assetsLoading, setAssetsLoading] = useState(false) // Start false - show empty state immediately
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
    const assetsLoadedRef = useRef<string | null>(null) // Track loaded assets to prevent unnecessary reloads
    
    // Move to Space state
    const [showMoveToSpaceDropdown, setShowMoveToSpaceDropdown] = useState(false)
    const [modalSpaces, setModalSpaces] = useState<any[]>([])
    const [loadingModalSpaces, setLoadingModalSpaces] = useState(false)
    const [movingToSpace, setMovingToSpace] = useState(false)
    const [newSpaceName, setNewSpaceName] = useState('')
    const [showCreateSpace, setShowCreateSpace] = useState(false)
    const moveDropdownRef = useRef<HTMLDivElement>(null)
    
    // Load spaces when dropdown opens
    useEffect(() => {
      const loadSpaces = async () => {
        if (!showMoveToSpaceDropdown || !token) return
        setLoadingModalSpaces(true)
        try {
          const res = await fetch('/api/spaces', {
            headers: { Authorization: `Bearer ${token}` }
          })
          if (res.ok) {
            const json = await res.json()
            setModalSpaces(json.spaces || [])
          }
        } catch (err) {
          console.error('Failed to load spaces:', err)
        } finally {
          setLoadingModalSpaces(false)
        }
      }
      loadSpaces()
    }, [showMoveToSpaceDropdown, token])
    
    // Close dropdown when clicking outside
    useEffect(() => {
      const handleClickOutside = (e: MouseEvent) => {
        if (moveDropdownRef.current && !moveDropdownRef.current.contains(e.target as Node)) {
          setShowMoveToSpaceDropdown(false)
          setShowCreateSpace(false)
          setNewSpaceName('')
        }
      }
      if (showMoveToSpaceDropdown) {
        document.addEventListener('mousedown', handleClickOutside)
      }
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [showMoveToSpaceDropdown])
    
    // Handle move to space
    const handleMoveToSpace = async (spaceId: string, spaceName: string) => {
      if (!token || movingToSpace) return
      setMovingToSpace(true)
      try {
        const res = await fetch(`/api/spaces/move/${item.id}`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ spaceId })
        })
        if (res.ok) {
          addToast({ type: 'success', message: `Moved to ${spaceName}`, duration: 3000 })
          setShowMoveToSpaceDropdown(false)
          fetchContent(false) // Refresh library (item will be removed from library view)
          onClose()
        } else {
          throw new Error('Failed to move')
        }
      } catch (err) {
        console.error('Failed to move to space:', err)
        addToast({ type: 'error', message: 'Failed to move content', duration: 3000 })
      } finally {
        setMovingToSpace(false)
      }
    }
    
    // Handle create new space and move
    const handleCreateSpaceAndMove = async () => {
      if (!token || !newSpaceName.trim() || movingToSpace) return
      setMovingToSpace(true)
      try {
        // Create the space first
        const createRes = await fetch('/api/spaces', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ name: newSpaceName.trim() })
        })
        if (!createRes.ok) throw new Error('Failed to create space')
        const { space } = await createRes.json()
        
        // Now move the content to the new space
        const moveRes = await fetch(`/api/spaces/move/${item.id}`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ spaceId: space.id })
        })
        if (moveRes.ok) {
          addToast({ type: 'success', message: `Moved to ${space.name}`, duration: 3000 })
          setShowMoveToSpaceDropdown(false)
          setShowCreateSpace(false)
          setNewSpaceName('')
          fetchContent(false)
          onClose()
        } else {
          throw new Error('Failed to move')
        }
      } catch (err) {
        console.error('Failed to create space and move:', err)
        addToast({ type: 'error', message: 'Failed to create space', duration: 3000 })
      } finally {
        setMovingToSpace(false)
      }
    }
    
    // Track loaded connections to prevent unnecessary reloads
    const connectionsLoadedRef = useRef<string | null>(null)
    
    // Load connections for Connect tab (related content + entities)
    useEffect(() => {
      const loadConnections = async () => {
        if (!token || !item?.id) return
        
        // Prevent duplicate calls for the same content
        if (connectionsLoadedRef.current === item.id) return
        connectionsLoadedRef.current = item.id
        
        try {
          const res = await fetch(`/api/content/${item.id}/connections?limit=5`, {
            headers: { Authorization: `Bearer ${token}` }
          })
          if (res.ok) {
            const json = await res.json()
            const data = json.data || {}
            
            // Filter out assets (notes, screenshots) - only show web/youtube content
            const filteredContent = (data.relatedContent || []).filter((related: any) => {
              const isAsset = related.tags?.includes('note') || 
                             related.tags?.includes('screenshot') || 
                             related.metadata?.type === 'note' || 
                             related.metadata?.type === 'screenshot' ||
                             related.category === 'screenshot'
              return !isAsset
            })
            setRelatedContentModal(filteredContent.slice(0, 5))
            
            // Set entities (already limited to 5 high-relevance by backend)
            setConnectionEntities(data.entities || [])
          }
        } catch (err) {
          console.error('Failed to load connections:', err)
        } finally {
          setRelatedLoading(false)
        }
      }
      loadConnections()
    }, [item?.id, token])
    
    // Load knowledge gaps for Connect tab
    const gapsLoadedRef = useRef<string | null>(null)
    
    // Reset ref when modal closes
    useEffect(() => {
      return () => {
        gapsLoadedRef.current = null
      }
    }, [])
    
    useEffect(() => {
      const loadKnowledgeGaps = async () => {
        if (!token || !item?.id) return
        
        // Prevent duplicate calls for the same content
        if (gapsLoadedRef.current === item.id) return
        
        gapsLoadedRef.current = item.id
        setGapsLoading(true)
        
        try {
          const res = await fetch(`/api/content/${item.id}/knowledge-gaps`, {
            method: 'POST',
            headers: { 
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          })
          if (res.ok) {
            const json = await res.json()
            const suggestions = json.data?.suggestions || []
            // Handle both old format (strings) and new format (objects with topic/type)
            const normalizedSuggestions = suggestions.map((s: any) => {
              if (typeof s === 'string') {
                return { topic: s, type: 'complementary' }
              }
              return s
            })
            setKnowledgeGaps(normalizedSuggestions)
          }
        } catch (err) {
          console.error('Failed to load knowledge gaps:', err)
        } finally {
          setGapsLoading(false)
        }
      }
      loadKnowledgeGaps()
    }, [item?.id, token])

    const handleDeleteClick = () => {
      setShowDeleteConfirm(true)
    }

    const handleDelete = async () => {
      if (!token) return

      try {
        const response = await fetch(`/api/content/${item.id}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })

        if (!response.ok) throw new Error('delete failed')

        setShowDeleteConfirm(false)
        fetchContent(false) // Refresh list without loading spinner
        onClose()
      } catch (err) {
        console.error('Failed to delete:', err)
        addToast({
          type: 'error',
          title: 'Error',
          message: 'Failed to delete content. Please try again.'
        })
        setShowDeleteConfirm(false)
      }
    }

    const handleSendChat = async () => {
      if (!chatInput.trim() || isSendingChat) return
      
      const userMessage = chatInput.trim()
      setChatInput('')
      setChatMessages(prev => [...prev, { role: 'user', content: userMessage }])
      setIsSendingChat(true)

      try {
        const response = await fetch('/api/chat/content', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            message: userMessage,
            contentId: item.id,
            contentContext: {
              title: item.title,
              summary: item.summary,
              takeaways: item.takeaways
            }
          })
        })

        if (!response.ok) throw new Error('Chat failed')
        
        const data = await response.json()
        setChatMessages(prev => [...prev, { role: 'assistant', content: data.response }])
      } catch (error) {
        console.error('Chat error:', error)
        setChatMessages(prev => [...prev, { 
          role: 'assistant', 
          content: 'Sorry, I encountered an error. Please try again.' 
        }])
      } finally {
        setIsSendingChat(false)
      }
    }

    const handleQuickHelp = async (helpType: 'explain_simple' | 'key_concepts' | 'prerequisites' | 'quiz' | 'examples') => {
      if (isSendingChat) return
      
      const helpLabels = {
        explain_simple: 'Explain this simply',
        key_concepts: 'What are the key concepts?',
        prerequisites: 'What should I know first?',
        quiz: 'Quiz me on this',
        examples: 'Show me examples'
      }
      
      const userMessage = helpLabels[helpType]
      setChatMessages(prev => [...prev, { role: 'user', content: userMessage }])
      setIsSendingChat(true)

      try {
        const response = await fetch(`/api/chat/content/${item.id}/help`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ helpType })
        })

        if (!response.ok) throw new Error('Help request failed')
        
        const result = await response.json()
        // Backend returns { success: true, data: { content, helpType, contentId, contentTitle } }
        const aiResponse = result.data?.content || result.content || 'No response received'
        setChatMessages(prev => [...prev, { role: 'assistant', content: aiResponse }])
      } catch (error) {
        console.error('Quick help error:', error)
        setChatMessages(prev => [...prev, { 
          role: 'assistant', 
          content: 'Sorry, I encountered an error. Please try again.' 
        }])
      } finally {
        setIsSendingChat(false)
      }
    }

    useEffect(() => {
      const loadAssets = async () => {
        if (!token || !item?.id) return
        
        // Prevent duplicate calls for the same content
        if (assetsLoadedRef.current === item.id) return
        assetsLoadedRef.current = item.id
        
        try {
          const res = await fetch(`/api/content/${item.id}/assets`, {
            headers: { Authorization: `Bearer ${token}` }
          })
          if (!res.ok) throw new Error('Failed to fetch assets')
          const json = await res.json()
          const assets = Array.isArray(json?.data) ? json.data : []
          setLinkedAssets(assets)
        } catch {
          setLinkedAssets([])
        } finally {
          setAssetsLoading(false)
        }
      }
      loadAssets()
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [item?.id, token])

    return (
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center"
        onClick={onClose}
      >
        <div
          className="bg-white dark:bg-gray-900 w-full h-full flex flex-col overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
            {/* Header with close button */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3 flex-1 min-w-0 mr-4">
                {/* Source link - top left */}
                <a
                  href={item.url || '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 hover:text-blue-500 dark:hover:text-blue-400 transition-colors group"
                  title="View original source"
                >
                  {contentType === 'youtube' ? (
                    <SourceIcon className="w-4 h-4" />
                  ) : (
                    <img src="/worldwide.png" alt="Web" className="w-4 h-4 object-contain" />
                  )}
                  <span className="group-hover:underline">{contentType === 'youtube' ? 'YouTube' : 'Web'}</span>
                  <ExternalLink className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity" />
                </a>
              </div>
              
              <div className="flex items-center gap-3">
                {/* Reader/Writer Toggle - Modern pill style */}
                <div className="flex items-center bg-gray-100 dark:bg-gray-800 rounded-full p-1">
                  <button
                    onClick={() => {
                      setLocalEditMode(false)
                      setLocalSummary(item.summary)
                      setLocalTakeaways([...item.takeaways])
                    }}
                    className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-full transition-all ${
                      !localEditMode 
                        ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm' 
                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                    }`}
                  >
                    <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M2 6h20M2 10h20M2 14h12" strokeLinecap="round"/>
                    </svg>
                    Reader
                  </button>
                  <button
                    onClick={() => setLocalEditMode(true)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-full transition-all ${
                      localEditMode 
                        ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm' 
                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                    }`}
                  >
                    <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Writer
                  </button>
                </div>

                {/* Save button - only in writer mode */}
                {localEditMode && (
                  <button
                    onClick={handleSave}
                    disabled={isSaving}
                    className="px-4 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-full transition-colors disabled:opacity-50"
                  >
                    {isSaving ? 'Saving...' : 'Save'}
                  </button>
                )}
                
                {/* Move to Space button with dropdown */}
                <div className="relative" ref={moveDropdownRef}>
                  <button
                    onClick={() => setShowMoveToSpaceDropdown(!showMoveToSpaceDropdown)}
                    className="p-2 text-gray-400 hover:text-blue-500 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-all"
                    title="Move to Space"
                  >
                    <FolderInput className="w-4 h-4" />
                  </button>
                  
                  {/* Dropdown */}
                  {showMoveToSpaceDropdown && (
                    <div className="absolute right-0 top-full mt-2 w-64 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden z-50">
                      <div className="p-3 border-b border-gray-100 dark:border-gray-700">
                        <p className="text-sm font-medium text-gray-900 dark:text-white">Move to Space</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Content will be moved from Library</p>
                      </div>
                      
                      <div className="max-h-48 overflow-y-auto">
                        {loadingModalSpaces ? (
                          <div className="p-4 text-center">
                            <div className="flex items-center justify-center gap-1.5">
                              <span className="w-2 h-2 rounded-full bg-blue-400 animate-[bounce_1s_ease-in-out_infinite]" />
                              <span className="w-2 h-2 rounded-full bg-blue-500 animate-[bounce_1s_ease-in-out_infinite]" style={{ animationDelay: '0.15s' }} />
                              <span className="w-2 h-2 rounded-full bg-blue-600 animate-[bounce_1s_ease-in-out_infinite]" style={{ animationDelay: '0.3s' }} />
                            </div>
                          </div>
                        ) : modalSpaces.length === 0 ? (
                          <div className="p-4 text-center text-sm text-gray-500 dark:text-gray-400">
                            No spaces yet
                          </div>
                        ) : (
                          modalSpaces.map(space => (
                            <button
                              key={space.id}
                              onClick={() => handleMoveToSpace(space.id, space.name)}
                              disabled={movingToSpace}
                              className="w-full px-4 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors flex items-center gap-3 disabled:opacity-50"
                            >
                              <div className={`w-8 h-8 rounded-lg flex items-center justify-center bg-${space.color || 'blue'}-100 dark:bg-${space.color || 'blue'}-900/30`}>
                                <FolderInput className={`w-4 h-4 text-${space.color || 'blue'}-600 dark:text-${space.color || 'blue'}-400`} />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{space.name}</p>
                                <p className="text-xs text-gray-500 dark:text-gray-400">{space.itemCount || 0} items</p>
                              </div>
                            </button>
                          ))
                        )}
                      </div>
                      
                      {/* Create new space section */}
                      <div className="border-t border-gray-100 dark:border-gray-700">
                        {!showCreateSpace ? (
                          <button
                            onClick={() => setShowCreateSpace(true)}
                            className="w-full px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors flex items-center gap-3 text-blue-600 dark:text-blue-400"
                          >
                            <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-blue-50 dark:bg-blue-900/30">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                              </svg>
                            </div>
                            <span className="text-sm font-medium">Create new space</span>
                          </button>
                        ) : (
                          <div className="p-3">
                            <input
                              type="text"
                              value={newSpaceName}
                              onChange={(e) => setNewSpaceName(e.target.value)}
                              placeholder="Space name..."
                              className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 dark:text-white"
                              autoFocus
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && newSpaceName.trim()) {
                                  handleCreateSpaceAndMove()
                                } else if (e.key === 'Escape') {
                                  setShowCreateSpace(false)
                                  setNewSpaceName('')
                                }
                              }}
                            />
                            <div className="flex gap-2 mt-2">
                              <button
                                onClick={() => {
                                  setShowCreateSpace(false)
                                  setNewSpaceName('')
                                }}
                                className="flex-1 px-2 py-1.5 text-xs text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                              >
                                Cancel
                              </button>
                              <button
                                onClick={handleCreateSpaceAndMove}
                                disabled={!newSpaceName.trim() || movingToSpace}
                                className="flex-1 px-2 py-1.5 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 whitespace-nowrap"
                              >
                                {movingToSpace ? 'Creating...' : 'Create & Move'}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Delete icon */}
                <button
                  onClick={handleDeleteClick}
                  className="p-2 text-gray-400 hover:text-red-500 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-all"
                  title="Delete content"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
                
                {/* Close button */}
                <button
                  onClick={onClose}
                  className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-all"
                  title="Close"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Two-column layout */}
            <div className="flex-1 flex overflow-hidden relative">
              {/* Left side - Content (scrollable) */}
              <div 
                className="overflow-y-auto p-8 transition-all duration-200"
                style={{ width: isMaximized ? '0' : undefined, flex: isMaximized ? '0 0 0' : '1 1 0%', opacity: isMaximized ? 0 : 1 }}
              >
                {/* Title */}
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 leading-snug tracking-tight">
                  {item.title}
                </h1>

                {/* Thumbnail */}
                {thumbnail && (
                  <div className="relative h-64 mb-6 rounded-xl overflow-hidden">
                    <img 
                      src={thumbnail} 
                      alt={item.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}

                {/* Meta Info */}
                <div className="flex items-center gap-4 text-sm text-gray-400 dark:text-gray-500 mb-8 flex-wrap">
                  {formatFullDate(item.createdAt) && (
                  <div className="flex items-center gap-1.5">
                    <Calendar className="w-3.5 h-3.5" />
                      <span className="text-xs">{formatFullDate(item.createdAt)}</span>
                  </div>
                  )}
                  <div className="flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-blue-400"></span>
                    <span className="text-xs font-medium text-gray-500 dark:text-gray-400">{item.category || 'Other'}</span>
                  </div>
                </div>

                {/* Summary */}
                <div className="mb-8">
                  <p className="text-[11px] font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500 mb-3">Summary</p>
                  {!localEditMode ? (
                    <div className="relative">
                      <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-gradient-to-b from-blue-400 to-blue-200 dark:from-blue-500 dark:to-blue-800 rounded-full" />
                      <p className="pl-4 text-gray-700 dark:text-gray-300 leading-[1.85] text-[15px]">
                        {localSummary}
                      </p>
                    </div>
                  ) : (
                    <textarea
                      value={localSummary}
                      onChange={(e) => setLocalSummary(e.target.value)}
                      className="w-full p-3 text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800 border-0 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 leading-relaxed resize-none min-h-[120px]"
                      placeholder="Edit summary..."
                    />
                  )}
                </div>

                {/* Linked Assets */}
                <div className="mb-8">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-[11px] font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500">Linked Assets</p>
                  </div>
                  {linkedAssets.length === 0 ? (
                    <p className="text-sm text-gray-400 dark:text-gray-500 italic">No linked notes or screenshots yet.</p>
                  ) : (
                    <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1">
                      {linkedAssets.slice(0, 6).map((a) => (
                        <button
                          key={a.id}
                          onClick={() => openAssetViewer(a)}
                          className="flex-shrink-0 w-[220px] text-left group hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-xl px-3 py-2.5 border border-gray-100 dark:border-gray-700 hover:border-blue-200 dark:hover:border-blue-700 transition-all"
                        >
                          <div className="flex-1 min-w-0">
                            <div className="text-xs font-medium text-gray-900 dark:text-white truncate">
                              {(a.metadata?.type === 'screenshot' || a.tags?.includes('screenshot')) ? '📸 ' : '📝 '}
                              {a.title || 'Untitled'}
                            </div>
                            {(a.summary || a.content) && (
                              <div className="text-xs text-gray-400 dark:text-gray-500 truncate mt-0.5">{a.summary || a.content}</div>
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Takeaways */}
                <div className="mb-8">
                  <p className="text-[11px] font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500 mb-4">Key Takeaways</p>
                  {!localEditMode ? (
                    <div className="space-y-3">
                      {localTakeaways.map((takeaway, index) => (
                        <div key={index} className="flex items-start gap-3 group">
                          <span className="flex-shrink-0 w-5 h-5 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 text-[10px] font-bold flex items-center justify-center mt-0.5 group-hover:bg-blue-100 dark:group-hover:bg-blue-900/30 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                            {index + 1}
                          </span>
                          <span className="text-gray-700 dark:text-gray-300 leading-relaxed text-[15px]">{capitalizeFirstLetter(takeaway)}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <textarea
                      value={localTakeaways.join('\n\n')}
                      onChange={(e) => {
                        const newTakeaways = e.target.value.split('\n\n').filter(t => t.trim())
                        setLocalTakeaways(newTakeaways.length > 0 ? newTakeaways : [''])
                      }}
                      className="w-full p-3 text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800 border-0 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 leading-relaxed resize-none min-h-[200px]"
                      placeholder="Enter takeaways separated by blank lines..."
                    />
                  )}
                </div>

                {/* Tags */}
                {item.tags.length > 0 && (
                  <div className="mb-6">
                    <p className="text-[11px] font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500 mb-3">Tags</p>
                    <div className="flex flex-wrap gap-2">
                      {item.tags.map((tag, index) => (
                        <span key={index} className="px-2.5 py-1 bg-gray-50 dark:bg-gray-800 text-gray-500 dark:text-gray-400 rounded-full text-xs border border-gray-100 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-500 transition-colors cursor-default">
                          #{tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}


              </div>
              
              {/* Resizable divider */}
              {!isMaximized && (
                <div
                  onMouseDown={handleMouseDown}
                  className={`w-1 hover:w-1.5 bg-gray-200 dark:bg-gray-700 hover:bg-blue-400 dark:hover:bg-blue-500 cursor-col-resize transition-all flex-shrink-0 ${isDragging ? 'bg-blue-500 w-1.5' : ''}`}
                  title="Drag to resize"
                />
              )}

              {/* Right side - Connect & Chat */}
              <div 
                className="flex flex-col bg-gray-50 dark:bg-gray-800 min-h-0 transition-all duration-200"
                style={{ width: isMaximized ? '100%' : `${panelWidth}px` }}
              >
                {/* Tab switcher with maximize button */}
                <div className="flex items-center border-b border-gray-200 dark:border-gray-700 flex-shrink-0 px-3 pt-2 gap-1">
                  <button
                    onClick={() => setActiveTab('connect')}
                    className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-full transition-all mb-2 ${
                      activeTab === 'connect'
                        ? 'bg-gray-900 dark:bg-white text-white dark:text-gray-900 shadow-sm'
                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                  >
                    <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="6" cy="12" r="2"/><circle cx="18" cy="6" r="2"/><circle cx="18" cy="18" r="2"/>
                      <path d="M8 12h8M16 6l-8 4.5M16 18l-8-4.5" strokeLinecap="round"/>
                    </svg>
                    Relations
                  </button>
                  <button
                    onClick={() => setActiveTab('chat')}
                    className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-full transition-all mb-2 ${
                      activeTab === 'chat'
                        ? 'bg-gray-900 dark:bg-white text-white dark:text-gray-900 shadow-sm'
                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                  >
                    <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Chat
                  </button>
                  {/* Maximize/Minimize button */}
                  <button
                    onClick={() => setIsMaximized(!isMaximized)}
                    className="ml-auto p-1.5 mb-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-all"
                    title={isMaximized ? 'Restore panel' : 'Maximize panel'}
                  >
                    {isMaximized ? (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 9V4.5M9 9H4.5M9 9L3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5m0-4.5l5.25 5.25" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                      </svg>
                    )}
                  </button>
                </div>

                {/* Tab content */}
                {activeTab === 'connect' ? (
                  <div className="flex-1 p-4 min-h-0 overflow-y-auto">
                    {/* Connections Section - Saved Content + Entities */}
                    <div className="mb-6">
                      <div className="flex items-center gap-2 mb-3">
                        <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                        </svg>
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Connected Entities</h3>
                      </div>
                      
                      {(relatedContent.length > 0 || connectionEntities.length > 0) ? (
                        <div className="space-y-2">
                          {/* Saved Content Items */}
                          {relatedContent.map((related: any) => (
                            <button
                              key={related.id}
                              onClick={() => {
                                onClose()
                                navigate(`/library?highlight=${related.id}`)
                              }}
                              className="w-full text-left p-3 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-blue-300 dark:hover:border-blue-600 transition-all group"
                            >
                              <div className="flex items-start gap-3">
                                <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center">
                                  {getContentType(related.url) === 'youtube' ? (
                                    <Youtube className="w-4 h-4 text-red-500" />
                                  ) : (
                                    <img src="/worldwide.png" alt="Web" className="w-4 h-4 object-contain" />
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-gray-900 dark:text-white truncate group-hover:text-blue-600 dark:group-hover:text-blue-400">
                                    {related.title}
                                  </p>
                                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                    {related.category} • {formatDate(related.created_at)}
                                  </p>
                                </div>
                                <svg className="w-4 h-4 text-gray-300 group-hover:text-blue-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                              </div>
                            </button>
                          ))}
                          
                          {/* Entity Items - Clickable, opens Google search */}
                          {connectionEntities.map((entity: any, idx: number) => (
                            <a
                              key={`entity-${idx}`}
                              href={`https://www.google.com/search?q=${encodeURIComponent(entity.name)}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="w-full text-left p-3 bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-gray-300 dark:hover:border-gray-600 transition-all group"
                            >
                              <div className="flex items-center gap-3">
                                <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                                  <svg className="w-4 h-4 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                                  </svg>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-white">
                                    {entity.name}
                                  </p>
                                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                    {entity.type?.toLowerCase()}
                                  </p>
                                </div>
                                <svg className="w-4 h-4 text-gray-300 group-hover:text-gray-500 dark:group-hover:text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                </svg>
                              </div>
                            </a>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500 dark:text-gray-400 italic">
                          No connections found yet.
                        </p>
                      )}
                    </div>
                    
                    {/* Knowledge Gaps Section - UNTOUCHED */}
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <svg className="w-5 h-5 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                        </svg>
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">To Deepen Your Understanding</h3>
                      </div>
                      
                      {gapsLoading ? (
                        <div className="flex items-center gap-2 py-3">
                          <span 
                            className="text-sm font-medium text-transparent bg-clip-text animate-shimmer"
                            style={{
                              backgroundImage: 'linear-gradient(90deg, #3b82f6 0%, #1d4ed8 20%, #3b82f6 40%, #3b82f6 100%)',
                              backgroundSize: '200% 100%',
                              animation: 'shimmer 1.5s ease-in-out infinite'
                            }}
                          >
                            Thinking...
                          </span>
                          <style>{`
                            @keyframes shimmer {
                              0% { background-position: 200% 0; }
                              100% { background-position: -200% 0; }
                            }
                          `}</style>
                        </div>
                      ) : knowledgeGaps.length > 0 ? (
                        <div className="space-y-2 animate-fadeIn">
                          <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                            Click to search and explore:
                          </p>
                          {knowledgeGaps.map((gap, idx) => {
                            return (
                              <a
                                key={idx}
                                href={`https://www.google.com/search?q=${encodeURIComponent(gap.topic)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-start gap-2 p-2.5 bg-amber-50 dark:bg-amber-900/10 border border-amber-100 dark:border-amber-800/30 rounded-lg hover:bg-amber-100 dark:hover:bg-amber-900/20 hover:border-amber-200 dark:hover:border-amber-700/50 transition-all cursor-pointer group"
                                style={{ 
                                  animation: `fadeSlideIn 0.3s ease-out ${idx * 0.1}s both`
                                }}
                              >
                                <div className="flex-1 min-w-0">
                                  <span className="text-sm text-gray-700 dark:text-gray-300 group-hover:text-amber-700 dark:group-hover:text-amber-300">
                                    {gap.topic}
                                  </span>
                                </div>
                                <ExternalLink className="w-3.5 h-3.5 text-gray-400 group-hover:text-amber-500 flex-shrink-0 mt-1" />
                              </a>
                            )
                          })}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500 dark:text-gray-400 italic">
                          No suggestions available.
                        </p>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="flex-1 flex flex-col min-h-0">
                    {/* Chat messages - scrollable area */}
                    <div className="flex-1 overflow-y-auto p-4 space-y-4 min-h-0">
                      {chatMessages.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-full text-center">
                          {/* Cute Bot Icon */}
                          <div className="mb-6">
                            <svg width="80" height="80" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <rect x="4" y="12" width="28" height="20" rx="6" fill="#dbeafe" stroke="#1e293b" strokeWidth="2"/>
                              <rect x="10" y="18" width="5" height="5" rx="2" fill="#1e293b"/>
                              <rect x="21" y="18" width="5" height="5" rx="2" fill="#1e293b"/>
                              <path d="M14 27c1.5 1.5 6.5 1.5 8 0" stroke="#1e293b" strokeWidth="2" strokeLinecap="round"/>
                              <line x1="18" y1="4" x2="18" y2="12" stroke="#1e293b" strokeWidth="2" strokeLinecap="round"/>
                              <circle cx="18" cy="4" r="2.5" fill="#fce7f3" stroke="#1e293b" strokeWidth="2"/>
                              <rect x="0" y="19" width="4" height="8" rx="2" fill="#93a3f8" stroke="#1e293b" strokeWidth="1.5"/>
                              <rect x="32" y="19" width="4" height="8" rx="2" fill="#93a3f8" stroke="#1e293b" strokeWidth="1.5"/>
                            </svg>
                          </div>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            Ask questions about this content
                          </p>
                        </div>
                      ) : (
                        <>
                          {chatMessages.map((msg, idx) => (
                            <div
                              key={idx}
                              className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start items-start gap-2'}`}
                            >
                              {/* Bot icon for assistant messages */}
                              {msg.role === 'assistant' && (
                                <div className="w-7 h-7 flex-shrink-0 mt-1">
                                  <svg width="28" height="28" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <rect x="4" y="12" width="28" height="20" rx="6" fill="#dbeafe" stroke="#1e293b" strokeWidth="2"/>
                                    <rect x="10" y="18" width="5" height="5" rx="2" fill="#1e293b"/>
                                    <rect x="21" y="18" width="5" height="5" rx="2" fill="#1e293b"/>
                                    <path d="M14 27c1.5 1.5 6.5 1.5 8 0" stroke="#1e293b" strokeWidth="2" strokeLinecap="round"/>
                                    <line x1="18" y1="4" x2="18" y2="12" stroke="#1e293b" strokeWidth="2" strokeLinecap="round"/>
                                    <circle cx="18" cy="4" r="2.5" fill="#fce7f3" stroke="#1e293b" strokeWidth="2"/>
                                    <rect x="0" y="19" width="4" height="8" rx="2" fill="#93a3f8" stroke="#1e293b" strokeWidth="1.5"/>
                                    <rect x="32" y="19" width="4" height="8" rx="2" fill="#93a3f8" stroke="#1e293b" strokeWidth="1.5"/>
                                  </svg>
                                </div>
                              )}
                              <div
                                className={`max-w-[80%] px-4 py-3 ${
                                  msg.role === 'user'
                                    ? 'bg-blue-50 dark:bg-blue-900/30 text-gray-900 dark:text-gray-100 rounded-2xl rounded-br-md'
                                    : 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-2xl rounded-bl-md border border-gray-100 dark:border-gray-700'
                                }`}
                              >
                                <p className="text-sm whitespace-pre-wrap leading-relaxed">{msg.content
                                  .replace(/\*\*(.*?)\*\*/g, '$1')
                                  .replace(/(?<!\*)\*([^*\n]+)\*(?!\*)/g, '$1')
                                  .replace(/^#{1,6}\s+/gm, '')
                                  .replace(/`([^`]+)`/g, '$1')
                                  .replace(/^[>\s]+/gm, '')
                                  .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
                                }</p>
                              </div>
                            </div>
                          ))}
                          {isSendingChat && (
                            <div className="flex justify-start">
                              <span 
                                className="text-sm font-medium text-transparent bg-clip-text"
                                style={{
                                  backgroundImage: 'linear-gradient(90deg, #3b82f6 0%, #1d4ed8 20%, #3b82f6 40%, #3b82f6 100%)',
                                  backgroundSize: '200% 100%',
                                  animation: 'shimmer 1.5s ease-in-out infinite'
                                }}
                              >
                                Thinking...
                              </span>
                              <style>{`
                                @keyframes shimmer {
                                  0% { background-position: 200% 0; }
                                  100% { background-position: -200% 0; }
                                }
                              `}</style>
                            </div>
                          )}
                          {/* Scroll anchor */}
                          <div ref={chatMessagesEndRef} />
                        </>
                      )}
                    </div>

                    {/* Quick help buttons - always visible above input */}
                    <div className="flex-shrink-0 px-4 py-2 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
                      <div className="flex flex-wrap gap-1.5 justify-center">
                        <button
                          onClick={() => handleQuickHelp('explain_simple')}
                          disabled={isSendingChat}
                          className="px-2.5 py-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Explain Simply
                        </button>
                        <button
                          onClick={() => handleQuickHelp('key_concepts')}
                          disabled={isSendingChat}
                          className="px-2.5 py-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Key Concepts
                        </button>
                        <button
                          onClick={() => handleQuickHelp('prerequisites')}
                          disabled={isSendingChat}
                          className="px-2.5 py-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Prerequisites
                        </button>
                        <button
                          onClick={() => handleQuickHelp('examples')}
                          disabled={isSendingChat}
                          className="px-2.5 py-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Examples
                        </button>
                        <button
                          onClick={() => handleQuickHelp('quiz')}
                          disabled={isSendingChat}
                          className="px-2.5 py-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Quiz Me
                        </button>
                      </div>
                    </div>

                    {/* Chat input - fixed at bottom */}
                    <div className="flex-shrink-0 p-4 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={chatInput}
                          onChange={(e) => setChatInput(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault()
                              handleSendChat()
                            }
                          }}
                          placeholder="Ask about this content..."
                          className="flex-1 px-4 py-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
                          disabled={isSendingChat}
                        />
                        <button
                          onClick={handleSendChat}
                          disabled={!chatInput.trim() || isSendingChat}
                          className="px-4 py-2.5 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Delete Confirmation Modal */}
          <ConfirmationModal
            isOpen={showDeleteConfirm}
            onClose={() => setShowDeleteConfirm(false)}
            onConfirm={handleDelete}
            title="Delete Content"
            message="Are you sure you want to delete this content? This action cannot be undone."
            confirmText="Delete"
            cancelText="Cancel"
            isDestructive={true}
          />
        </div>
    )
  }
  }, []) // Empty deps - the component function itself never changes

  // Open asset viewer for notes and screenshots
  const openAssetViewer = (item: ContentItem) => {
    const itemType = getItemType(item)
    
    if (itemType === 'note') {
      setSelectedNote({
        id: item.id,
        title: item.title,
        content: item.content || item.summary || item.metadata?.content || '',
        createdAt: item.createdAt || (item as any).created_at || item.metadata?.created_at,
        related_to_content_id: (item as any).related_to_content_id || (item as any).relatedToContentId || null,
        metadata: item.metadata
      })
      setNoteEditorOpen(true)
    } else if (itemType === 'screenshot') {
      setSelectedAsset({
        id: item.id,
        title: item.title,
        type: 'screenshot',
        imageUrl: item.metadata?.image_url,
        sourceTitle: item.metadata?.source_title,
        sourceUrl: item.metadata?.source_url || item.url,
        createdAt: item.createdAt || item.metadata?.created_at,
        metadata: item.metadata
      })
      setAssetViewerOpen(true)
    } else {
      // Regular content - open the content modal
      toggleExpand(item.id)
    }
  }

  // Modern ContentTile Component - uses unified ContentCard
  const ContentTile = ({ item }: { item: ContentItem }) => {
    const isSelected = selectedItems.has(item.id)
    const thumbnail = getThumbnail(item)
    const learningPaths = contentLearningPaths[item.id] || []
    
      return (
      <ContentCard
        id={item.id}
        title={item.title}
        summary={item.summary}
        url={item.url}
        thumbnail={thumbnail || undefined}
        createdAt={item.createdAt}
        category={item.category}
        duration={item.metadata?.duration}
        metadata={item.metadata}
        tags={item.tags}
        isSelected={isSelected}
        selectionMode={selectionMode}
        highlightId={highlightId}
        learningPaths={learningPaths}
        onSelect={() => toggleSelection(item.id)}
        onClick={() => openAssetViewer(item)}
      />
    )
  }

  // Find related content based on shared tags
  useEffect(() => {
    if (currentItem && contentItems.length > 0) {
      const related = contentItems
        .filter(item => 
          item.id !== currentItem.id && 
          item.tags.some(tag => currentItem.tags.includes(tag))
        )
        .slice(0, 3)
      setRelatedContent(related)
    } else {
      setRelatedContent([])
    }
  }, [currentItem, contentItems])

  // Handle highlighting from knowledge graph navigation
  useEffect(() => {
    // Only handle if we have a highlightId and haven't handled this one yet
    if (highlightId && highlightHandledRef.current !== highlightId) {
      console.log('Highlight requested:', highlightId);
      
      const itemToHighlight = contentItems.find(item => item.id === highlightId);
      console.log('Item found in contentItems:', !!itemToHighlight);
      
      if (itemToHighlight) {
        // Mark as handled immediately to prevent re-runs
        highlightHandledRef.current = highlightId;
        
        // Check if it's a screenshot or note FIRST - use helper functions for consistency
        const isScreenshotItem = isScreenshot(itemToHighlight);
        const isNoteItem = isNote(itemToHighlight);
        
        console.log('Is screenshot:', isScreenshotItem, 'Is note:', isNoteItem);
        
        if (isScreenshotItem) {
          console.log('Opening screenshot viewer');
          setSelectedAsset({
            id: itemToHighlight.id,
            title: itemToHighlight.title,
            type: 'screenshot',
            imageUrl: itemToHighlight.metadata?.image_url,
            sourceTitle: itemToHighlight.metadata?.source_title,
            sourceUrl: itemToHighlight.metadata?.source_url || itemToHighlight.url,
            createdAt: itemToHighlight.createdAt,
            metadata: itemToHighlight.metadata
          });
          setAssetViewerOpen(true);
          setIsHighlightNavigation(false);
        } else if (isNoteItem) {
          console.log('Opening note editor');
          // Open note in editor modal (edit mode)
          setSelectedNote({
            id: itemToHighlight.id,
            title: itemToHighlight.title,
            content: itemToHighlight.content || '',
            createdAt: itemToHighlight.createdAt,
            related_to_content_id: itemToHighlight.related_to_content_id,
            metadata: itemToHighlight.metadata
          });
          setNoteEditorOpen(true);
          setIsHighlightNavigation(false);
        } else {
          console.log('Opening regular content');
          // Obsidian-style: Open the modal directly - no scroll, no intermediate state
          // Set both states synchronously so modal opens immediately
          setExpandedItems(prev => new Set(prev).add(highlightId));
          setCurrentItem(itemToHighlight);
          // Clear highlight navigation flag after modal is set to open
          setIsHighlightNavigation(false);
        }
        
        // Clear the highlight parameter from URL using history.replaceState (no re-render)
        const url = new URL(window.location.href);
        url.searchParams.delete('highlight');
        url.searchParams.delete('content'); // Also clear 'content' param
        window.history.replaceState({}, '', url.pathname + (url.search || ''));
      } else if (contentItems.length > 0) {
        // If not found after content loaded, try fetching directly
        // This handles content outside the current 100-item window
        console.log('Fetching content directly:', highlightId);
        highlightHandledRef.current = highlightId;
        
        const fetchContentDirectly = async () => {
          try {
            const response = await fetch(`/api/content/${highlightId}`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            
            console.log('Content fetch response:', response.status);
            
            if (response.ok) {
              const result = await response.json();
              const fetchedContent = result.data;
              
              console.log('Fetched content data:', fetchedContent);
              
              // Check if it's a screenshot or note
              const isScreenshotAsset = fetchedContent.tags?.includes('screenshot') || 
                                       fetchedContent.metadata?.type === 'screenshot' || 
                                       fetchedContent.category === 'screenshot' ||
                                       fetchedContent.title?.includes('Screenshot');
              const isNoteAsset = fetchedContent.tags?.includes('note') || 
                                 fetchedContent.metadata?.type === 'note' ||
                                 fetchedContent.title?.startsWith('Note:');
              
              console.log('Is screenshot:', isScreenshotAsset, 'Is note:', isNoteAsset);
              
              if (isScreenshotAsset) {
                console.log('Opening screenshot viewer');
                setSelectedAsset({
                  id: fetchedContent.id,
                  title: fetchedContent.title,
                  type: 'screenshot',
                  imageUrl: fetchedContent.metadata?.image_url,
                  sourceTitle: fetchedContent.metadata?.source_title,
                  sourceUrl: fetchedContent.metadata?.source_url || fetchedContent.url,
                  createdAt: fetchedContent.created_at,
                  metadata: fetchedContent.metadata
                });
                setAssetViewerOpen(true);
                setIsHighlightNavigation(false);
              } else if (isNoteAsset) {
                console.log('Opening note editor');
                setSelectedNote({
                  id: fetchedContent.id,
                  title: fetchedContent.title,
                  content: fetchedContent.content || '',
                  createdAt: fetchedContent.created_at,
                  related_to_content_id: fetchedContent.related_to_content_id,
                  metadata: fetchedContent.metadata
                });
                setNoteEditorOpen(true);
                setIsHighlightNavigation(false);
              } else {
                // Regular content (web/YouTube) - open in ContentModal
                console.log('Opening regular content in modal');
                // Convert API response to ContentItem format
                const contentItem: ContentItem = {
                  id: fetchedContent.id,
                  url: fetchedContent.url,
                  title: fetchedContent.title,
                  content: fetchedContent.content,
                  summary: fetchedContent.summary,
                  takeaways: fetchedContent.takeaways || [],
                  tags: fetchedContent.tags || [],
                  category: fetchedContent.category,
                  difficulty: fetchedContent.difficulty,
                  word_count: fetchedContent.word_count,
                  createdAt: fetchedContent.created_at,
                  related_to_content_id: fetchedContent.related_to_content_id,
                  metadata: fetchedContent.metadata
                };
                setCurrentItem(contentItem);
                setExpandedItems(prev => new Set(prev).add(fetchedContent.id));
                setIsHighlightNavigation(false);
              }
              
              // Clear the highlight parameter
              const url = new URL(window.location.href);
              url.searchParams.delete('highlight');
              url.searchParams.delete('content');
              window.history.replaceState({}, '', url.pathname + (url.search || ''));
            } else {
              console.error('Content not found:', highlightId);
              setIsHighlightNavigation(false);
            }
          } catch (error) {
            console.error('Failed to fetch content:', error);
            setIsHighlightNavigation(false);
          }
        };
        
        fetchContentDirectly();
      }
    }
  }, [highlightId, contentItems, token]);

  useEffect(() => {
    // Only fetch if we don't have content loaded yet, or if spaceId changed
    // This prevents unnecessary refetch when navigating from graph with highlight
    const shouldFetch = !initialLoadDone || contentItems.length === 0;
    
    if (shouldFetch) {
      fetchContent()
    }
    
    fetchSmartNotification()
    fetchUserStats()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spaceId, token])

  // Listen for content saved from extension (cross-tab communication via localStorage)
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'Memtrail_content_saved' && e.newValue) {
        console.log('📥 Content saved from extension, refreshing library...');
        // Small delay to ensure backend has processed the content
        setTimeout(() => {
          fetchContent(false); // Refresh without loading spinner
        }, 500);
        // Clear the flag
        localStorage.removeItem('Memtrail_content_saved');
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [token])

  // Auto-refresh when window regains focus (user comes back from extension)
  useEffect(() => {
    let lastFetchTime = Date.now();
    
    const handleFocus = () => {
      const timeSinceLastFetch = Date.now() - lastFetchTime;
      // Only refresh if it's been more than 5 seconds since last fetch
      if (timeSinceLastFetch > 5000) {
        console.log('👁️ Window focused, checking for new content...');
        fetchContent(false); // Refresh without loading spinner
        lastFetchTime = Date.now();
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [token])

  // Fetch user stats (streak)
  const fetchUserStats = async () => {
    if (!token) return
    try {
      const response = await fetch('/api/content/user-stats', {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      if (response.ok) {
        const data = await response.json()
        console.log('✅ User stats fetched:', data.data)
        setUserStats(data.data)
      } else {
        console.error('❌ User stats fetch failed:', response.status, response.statusText)
        // Set empty stats so we don't keep trying
        setUserStats({ streak: 0 })
      }
    } catch (error) {
      console.error('❌ Failed to fetch user stats:', error)
      // Set empty stats so we don't keep trying
      setUserStats({ streak: 0 })
    }
  }

  // Fetch spaces when move to space modal opens
  useEffect(() => {
    if (showMoveToSpace) {
      fetchSpaces()
    }
  }, [showMoveToSpace])

  // Fetch current space details when spaceId changes
  useEffect(() => {
    if (spaceId && token) {
      fetchCurrentSpace()
    } else {
      setCurrentSpace(null)
    }
  }, [spaceId, token])

  const fetchCurrentSpace = async () => {
    if (!spaceId || !token) return
    
    try {
      const response = await fetch(`/api/spaces/${spaceId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
      
      if (response.ok) {
        const data = await response.json()
        setCurrentSpace(data.space)
      }
    } catch (error) {
      console.error('Error fetching current space:', error)
    }
  }

  const fetchLearningPathsForContent = async (contentItemIds: string[]) => {
    if (!token || contentItemIds.length === 0) return
    
    try {
      console.log('🎯 Fetching learning paths for', contentItemIds.length, 'content items')
      const response = await fetch('/api/learning-paths/for-content', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ contentItemIds })
      })
      
      console.log('🎯 Learning paths response:', response.status)
      
      if (response.ok) {
        const result = await response.json()
        console.log('🎯 Learning paths data:', result.data)
        console.log('🎯 Number of items with paths:', Object.keys(result.data || {}).length)
        setContentLearningPaths(result.data || {})
      } else {
        console.error('🎯 Failed to fetch learning paths:', response.status, response.statusText)
      }
    } catch (error) {
      console.error('🎯 Error fetching learning paths for content:', error)
    }
  }

  const fetchContent = async (showLoading = true, loadMore = false) => {
    // Prevent double fetching
    if (fetchingRef.current) {
      console.log('Fetch already in progress, skipping...')
      return
    }
    
    fetchingRef.current = true
    
    // Only show loading spinner on very first load (never loaded before)
    if (showLoading && !initialLoadDone && !loadMore) {
      setIsLoading(true)
    }
    
    if (loadMore) {
      setLoadingMore(true)
    }
    
    try {
      console.log('Fetching content with token:', !!token, 'spaceId:', spaceId, 'loadMore:', loadMore)
      
      // Calculate page for load more
      const page = loadMore ? currentPage + 1 : 1
      
      // Use different endpoint based on whether we're filtering by space
      let endpoint: string
      if (spaceId) {
        endpoint = `/api/spaces/${spaceId}/items`
      } else {
        // Build query params for pagination and search
        const params = new URLSearchParams()
        params.set('limit', ITEMS_PER_PAGE.toString())
        params.set('page', page.toString())
        endpoint = `/api/content?${params.toString()}`
      }
      
      const response = await fetch(endpoint, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })

      console.log('Content fetch response:', response.status, response.statusText)

      if (response.ok) {
        const result = await response.json()
        console.log('Content data received:', result)

        // Handle different API response structures
        let rawItems = []
        if (spaceId) {
          // Spaces API returns { items: [...] }
          rawItems = result.items || []
        } else {
          // Content API returns { success: true, data: [...], pagination: {...} }
          rawItems = (result && result.success && Array.isArray(result.data)) ? result.data : []
          
          // Update pagination state
          if (result.pagination) {
            setTotalCount(result.pagination.total || 0)
            setHasMore(result.pagination.hasNext || false)
            setCurrentPage(page)
          }
        }
        
        // Transform items to ensure createdAt is properly set
        const items = rawItems.map((item: any) => ({
          ...item,
          createdAt: item.createdAt || item.created_at || item.metadata?.created_at || null,
          related_to_content_id: item.related_to_content_id ?? item.relatedToContentId ?? null,
          takeaways: item.takeaways || [],
          tags: item.tags || [],
          category: item.category || 'other',
          difficulty: item.difficulty || 'medium',
          word_count: item.word_count || 0
        }))
        
        // Append or replace items based on loadMore
        if (loadMore) {
          setContentItems(prev => [...prev, ...items])
        } else {
          setContentItems(items)
        }
        
        // Fetch learning paths for these content items
        if (items.length > 0) {
          fetchLearningPathsForContent(items.map((item: any) => item.id))
        }
      } else {
        console.error('Content fetch failed:', response.status, response.statusText)
        if (!loadMore) {
          setContentItems([])
        }
      }
    } catch (error) {
      console.error('Error fetching content:', error)
      if (!loadMore) {
        setContentItems([])
      }
    } finally {
      setIsLoading(false)
      setLoadingMore(false)
      setInitialLoadDone(true) // Mark that we've completed at least one load
      fetchingRef.current = false
    }
  }

  // Load more handler
  const handleLoadMore = () => {
    if (!loadingMore && hasMore) {
      fetchContent(false, true)
    }
  }

  const fetchSpaces = async () => {
    if (!token) return
    
    try {
      setLoadingSpaces(true)
      const response = await fetch('/api/spaces', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
      
      if (response.ok) {
        const data = await response.json()
        setSpaces(data.spaces || [])
      }
    } catch (error) {
      console.error('Error fetching spaces:', error)
    } finally {
      setLoadingSpaces(false)
    }
  }

  const fetchSmartNotification = async () => {
    if (!token) return
    
    try {
      const response = await fetch('/api/learning-paths/smart-notification', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
      
      if (response.ok) {
        const data = await response.json()
        if (data.success && data.data) {
          // Create unique key for this notification based on type and relevant data
          const notification = data.data
          let notificationKey = `${notification.type}`
          
          // Add specific identifiers to make key unique
          if (notification.data.pathId) {
            notificationKey += `_${notification.data.pathId}`
          } else if (notification.data.topic) {
            notificationKey += `_${notification.data.topic}`
          }
          
          // Only show if not dismissed
          if (!isNotificationDismissed(notificationKey)) {
            setSmartNotification(data.data)
          }
        }
      }
    } catch (error) {
      console.error('Error fetching smart notification:', error)
    }
  }

  const handleMoveToSpace = async (spaceId: string, spaceName: string) => {
    if (!token || selectedItems.size === 0) return
    
    try {
      const response = await fetch(`/api/spaces/${spaceId}/items`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          contentIds: Array.from(selectedItems)
        })
      })
      
      if (response.ok) {
        addToast({
          type: 'success',
          title: 'Items Moved',
          message: `Moved ${selectedItems.size} items to "${spaceName}"`
        })
        setShowMoveToSpace(false)
        setSelectedItems(new Set())
        setSelectionMode(false)
        // Refresh content if we're viewing a space
        if (spaceId === spaceId) {
          fetchContent(false)
        }
      } else {
        addToast({
          type: 'error',
          title: 'Move Failed',
          message: 'Failed to move items to space'
        })
      }
    } catch (error) {
      console.error('Error moving to space:', error)
      addToast({
        type: 'error',
        title: 'Move Failed',
        message: 'Failed to move items to space'
      })
    }
  }

  // Only show loading on very first load, not on subsequent navigations
  if (isLoading && !initialLoadDone) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <p className="text-gray-500 dark:text-gray-400 font-medium">Loading your library...</p>
        </div>
      </div>
    )
  }

  const categories = getCategories()
  const filteredContent = getFilteredContent()
  const groupedContent: any[] = [] // Grouped view removed

  return (
    <div className="max-w-7xl mx-auto p-6 dark:text-white">
      {/* Modern Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            {spaceId && (
              <button
                onClick={() => navigate('/library')}
                className="flex items-center gap-2 px-3 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                
              </button>
            )}
            <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">
              {spaceId ? `Space: ${currentSpace?.name || 'Loading...'}` : (() => {
                const hour = new Date().getHours()
                const greeting = hour >= 5 && hour < 12 ? 'Good morning' : 
                                 hour >= 12 && hour < 17 ? 'Good afternoon' : 
                                 hour >= 17 && hour < 21 ? 'Good evening' : 'Working late'
                const name = user?.name || (user?.email ? user.email.split('@')[0] : 'there')
                return `${greeting}, ${name}`
              })()}
            </h1>
            {/* Streak message - clean subtitle */}
            {!spaceId ? (
              userStats && userStats.streak > 0 ? (
                <p className="text-gray-600 dark:text-gray-400 mt-1">
                  🔥{userStats.streak}-day streak, {userStats.streak >= 7 ? 'Amazing consistency!' : userStats.streak >= 3 ? 'Keep the momentum going!' : 'Great start!'}
                </p>
              ) : contentItems.length === 0 ? (
                <p className="text-gray-600 dark:text-gray-400 mt-1">
                  Start saving content with the browser extension
                </p>
              ) : null
            ) : (
              <p className="text-gray-600 dark:text-gray-400 mt-1">
                <span className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                  {currentSpace?.itemCount || 0} items in this space
                </span>
              </p>
            )}
            </div>
          </div>

      </div>

        {/* Search and Filters - with right margin to avoid notification overlap */}
        <div className="flex flex-col lg:flex-row gap-4 mb-6 pr-0 lg:pr-96">
          {/* Search Bar */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 w-5 h-5" />
          <input
            type="text"
              placeholder="Search your knowledge library..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-gray-400 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 shadow-sm"
            />
          </div>
          
          {/* View Toggle */}
          <div className="flex items-center gap-2">
            <div className="flex bg-gray-100 rounded-lg p-1">
              {/* Select Button (iOS/macOS style) */}
              <button
                onClick={toggleSelectionMode}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  selectionMode
                    ? 'bg-blue-500 text-white shadow-md hover:bg-blue-600'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {selectionMode ? 'Done' : 'Select'}
              </button>
            </div>
          </div>
        </div>

        {/* Selection Bar */}
        {selectedItems.size > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between"
          >
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-blue-600" />
                <span className="font-medium text-gray-900">
                  {selectedItems.size} {selectedItems.size === 1 ? 'item' : 'items'} selected
                </span>
              </div>
              <button
                onClick={selectAll}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                Select All
              </button>
              <button
                onClick={deselectAll}
                className="text-sm text-gray-600 hover:text-gray-700 font-medium"
              >
                Deselect All
              </button>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowMoveToSpace(true)}
                className="flex items-center gap-2 px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors text-sm font-medium"
              >
                <FolderInput className="w-4 h-4" />
                Move to Space
              </button>
            </div>
          </motion.div>
        )}

        {/* Category Pills */}
        <div className="flex flex-wrap gap-2 mb-6">
          <button
            onClick={() => setSelectedCategory('all')}
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all memtrail-btn ${
              selectedCategory === 'all'
                ? 'bg-blue-500 text-white shadow-md border-2 border-blue-600'
                : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 border-2 border-gray-200 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
            }`}
          >
            All ({totalCount > 0 ? totalCount : contentItems.length})
          </button>
          {categories && categories.length > 0 && categories.map((category) => (
            <button
              key={category.name}
              onClick={() => setSelectedCategory(category.name)}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all border-2 memtrail-btn ${
                selectedCategory === category.name
                  ? `${category.color} shadow-md`
                  : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 border-gray-200 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
              }`}
            >
              {category.name}
            </button>
          ))}
        </div>

        {/* Source Filter */}
        <div className="flex items-center gap-3 mb-6">
          <span className="text-sm text-gray-600 dark:text-gray-400">Source:</span>
          <div className="flex gap-1 p-1 bg-gray-100 dark:bg-gray-800 rounded-lg">
            {['all', 'web', 'youtube'].map((source) => (
              <button
                key={source}
                onClick={() => setSelectedSource(source)}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-200 flex items-center gap-1.5 ${
                  selectedSource === source
                    ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                {source === 'youtube' ? <Youtube className="w-4 h-4" /> : 
                 source === 'web' ? <img src="/worldwide.png" alt="Web" className="w-4 h-4 object-contain" /> : 'All'}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content Display */}
      {filteredContent.length === 0 ? (
        <div className="text-center py-16">
          <div className="w-20 h-20 mx-auto mb-6 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center">
            <FolderInput className="h-10 w-10 text-gray-400" />
          </div>
          <h3 className="text-xl font-semibold text-gray-600 dark:text-gray-400 mb-2">
            {searchQuery ? 'No results found' : 'Your library is empty'}
          </h3>
          <p className="text-gray-500 dark:text-gray-400 mb-6 max-w-md mx-auto">
            {searchQuery
              ? 'Try adjusting your search terms or filters'
              : 'Install the browser extension and start saving interesting content'
            }
          </p>
          {!searchQuery && (
            <div className="text-sm text-gray-400 dark:text-gray-500">
              Install the Memtrail extension to start capturing knowledge
            </div>
          )}
        </div>
      ) : (
        /* Grid View */
        <div className="grid gap-3 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
          {filteredContent.map((item) => (
            <ContentTile key={item.id} item={item} />
          ))}
        </div>
      )}

      {/* Load More Button & Count */}
      {!spaceId && filteredContent.length > 0 && (
        <div className="mt-8 flex flex-col items-center gap-3">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Showing {contentItems.length} of {totalCount} items
          </p>
          {hasMore && (
            <button
              onClick={handleLoadMore}
              disabled={loadingMore}
              className="px-6 py-2.5 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {loadingMore ? (
                <>
                  <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-[bounce_1s_ease-in-out_infinite]" /><span className="w-1.5 h-1.5 rounded-full bg-gray-500 animate-[bounce_1s_ease-in-out_infinite]" style={{ animationDelay: '0.15s' }} /><span className="w-1.5 h-1.5 rounded-full bg-gray-600 animate-[bounce_1s_ease-in-out_infinite]" style={{ animationDelay: '0.3s' }} /></span>
                  Loading...
                </>
              ) : (
                <>Load More ({totalCount - contentItems.length} remaining)</>
              )}
            </button>
          )}
        </div>
      )}

      {/* Related Articles */}
      {currentItem && !isExpanded(currentItem.id) && (
        <div className="mt-12">
          <RelatedArticles contentId={currentItem.id} />
        </div>
      )}

      {/* Content Modal */}
      {currentItem && isExpanded(currentItem.id) && (
        <ContentModal 
          key={currentItem.id}
          item={currentItem} 
          onClose={() => {
            setExpandedItems(new Set())
            setCurrentItem(null)
          }} 
        />
      )}

      {/* Move to Space Modal */}
      {showMoveToSpace && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
          >
            <h3 className="text-xl font-bold text-gray-900 mb-4">Move to Space</h3>
            <p className="text-gray-600 text-sm mb-6">
              Select a space to move {selectedItems.size} {selectedItems.size === 1 ? 'item' : 'items'}
            </p>

            {/* Space List */}
            <div className="space-y-2 mb-6 max-h-64 overflow-y-auto">
              {loadingSpaces ? (
                <div className="flex items-center justify-center py-4">
                  <div className="flex items-center justify-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-blue-400 animate-[bounce_1s_ease-in-out_infinite]" />
                    <span className="w-2 h-2 rounded-full bg-blue-500 animate-[bounce_1s_ease-in-out_infinite]" style={{ animationDelay: '0.15s' }} />
                    <span className="w-2 h-2 rounded-full bg-blue-600 animate-[bounce_1s_ease-in-out_infinite]" style={{ animationDelay: '0.3s' }} />
                  </div>
                  <span className="ml-2 text-gray-600">Loading spaces...</span>
                </div>
              ) : spaces.length === 0 ? (
                <div className="text-center py-4 text-gray-500">
                  No spaces available. Create a space first.
                </div>
              ) : (
                spaces.map((space) => (
                  <button
                    key={space.id}
                    onClick={() => handleMoveToSpace(space.id, space.name)}
                    className="w-full flex items-center gap-3 p-3 rounded-lg border border-gray-200 hover:border-blue-500 hover:bg-blue-50 transition-all text-left"
                  >
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      space.color === 'blue' ? 'bg-blue-100' :
                      space.color === 'green' ? 'bg-green-100' :
                      space.color === 'purple' ? 'bg-purple-100' :
                      space.color === 'red' ? 'bg-red-100' :
                      space.color === 'yellow' ? 'bg-yellow-100' :
                      space.color === 'orange' ? 'bg-orange-100' :
                      space.color === 'pink' ? 'bg-pink-100' :
                      'bg-gray-100'
                    }`}>
                      <FolderInput className={`w-5 h-5 ${
                        space.color === 'blue' ? 'text-blue-600' :
                        space.color === 'green' ? 'text-green-600' :
                        space.color === 'purple' ? 'text-purple-600' :
                        space.color === 'red' ? 'text-red-600' :
                        space.color === 'yellow' ? 'text-yellow-600' :
                        space.color === 'orange' ? 'text-orange-600' :
                        space.color === 'pink' ? 'text-pink-600' :
                        'text-gray-600'
                      }`} />
                    </div>
                    <div className="flex-1">
                      <span className="font-medium text-gray-900">{space.name}</span>
                      <div className="text-sm text-gray-500">{space.itemCount} items</div>
                    </div>
                  </button>
                ))
              )}
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={() => setShowMoveToSpace(false)}
                className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
              >
                Cancel
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Asset Viewer Modal (for notes and screenshots) */}
      <AssetViewerModal
        isOpen={assetViewerOpen}
        onClose={() => {
          setAssetViewerOpen(false)
          setSelectedAsset(null)
        }}
        asset={selectedAsset}
        token={token || ''}
        onSave={async (id, content) => {
          // Refresh the content list to show updated note
          fetchContent(false)
          return true
        }}
        onDeleted={() => {
          // Refresh list after deletion
          fetchContent(false)
        }}
      />

      {/* Fabric-like Note Editor Modal */}
      <NoteEditorModal
        isOpen={noteEditorOpen}
        onClose={() => {
          setNoteEditorOpen(false)
          setSelectedNote(null)
        }}
        note={selectedNote}
        token={token || ''}
        onSaved={() => {
          // Refresh list so re-open shows persisted title/body
          fetchContent(false)
        }}
        onDeleted={() => {
          // Refresh list after deletion
          fetchContent(false)
        }}
      />

      {/* Toast Notifications */}
      <ToastContainer toasts={toasts} onClose={removeToast} />


      {/* Regular Chat Widget (floating) */}
      <ChatWidget />
      
      {/* Floating Action Buttons */}
      <FloatingActionButtons
        showAddItemOnly={true}
        onAddItem={async () => {
          const draftId = `draft-${Date.now()}`
          setSelectedNote({
            id: draftId,
            title: 'Untitled note',
            content: '',
            isDraft: true,
            metadata: {}
          })
          setNoteEditorOpen(true)
        }}
        onDiscover={() => setShowDiscoverModal(true)}
        hideSpaceOption={true}
      />

      {/* Discover Modal */}
      <DiscoverModal
        isOpen={showDiscoverModal}
        onClose={() => setShowDiscoverModal(false)}
        context="library"
      />

      {/* Fixed Top-Right Notifications - positioned at top right */}
      <div className="fixed top-4 right-4 z-50 w-80 space-y-3">
        {/* Continue Learning Card */}
        {(smartNotification && (smartNotification.type === 'continue' || smartNotification.type === 'new_path') && smartNotification.data.pathTitle) && (
          <div 
            className="group p-4 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl border border-blue-200 dark:border-blue-800 
                       hover:border-blue-300 dark:hover:border-blue-600 hover:shadow-lg shadow-md
                       transition-all duration-200 animate-in slide-in-from-right relative"
          >
            {/* Close button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                // Mark as dismissed before closing
                if (smartNotification) {
                  let notificationKey = `${smartNotification.type}`
                  if (smartNotification.data.pathId) {
                    notificationKey += `_${smartNotification.data.pathId}`
                  } else if (smartNotification.data.topic) {
                    notificationKey += `_${smartNotification.data.topic}`
                  }
                  markNotificationDismissed(notificationKey)
                }
                setSmartNotification(null);
              }}
              className="absolute top-1 right-1 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-white/50 dark:hover:bg-gray-800/50 rounded-lg transition-colors z-10"
              aria-label="Close"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <div 
              onClick={() => {
                console.log('📍 Navigating to learning path:', smartNotification.data.actionUrl);
                try {
                  navigate(smartNotification.data.actionUrl);
                } catch (error) {
                  console.error('❌ Navigation error:', error);
                }
              }}
              className="cursor-pointer"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  {/* Progress ring */}
                  <div className="relative w-10 h-10 flex-shrink-0">
                    <svg className="w-10 h-10 -rotate-90" viewBox="0 0 36 36">
                      <circle cx="18" cy="18" r="15" fill="none" stroke="currentColor" 
                              className="text-blue-100 dark:text-blue-900/50" strokeWidth="3" />
                      <circle cx="18" cy="18" r="15" fill="none" stroke="currentColor"
                              className="text-blue-600 dark:text-blue-400" strokeWidth="3" strokeLinecap="round"
                              strokeDasharray={`${(smartNotification.data.progress || 0) * 0.94} 94`} />
                    </svg>
                    <span className="absolute inset-0 flex items-center justify-center text-xs font-semibold text-blue-700 dark:text-blue-300">
                      {smartNotification.data.progress || 0}%
                    </span>
                  </div>
                  
                  {/* Content */}
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium text-blue-600 dark:text-blue-400">
                      {smartNotification.type === 'new_path' ? 'New trail' : 'Continue learning'}
                    </p>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white line-clamp-2">
                      {smartNotification.data.pathTitle}
                    </p>
                  </div>
                </div>
                
                {/* Arrow */}
                <div className="flex items-center gap-1 text-blue-600 dark:text-blue-400 group-hover:text-blue-700 dark:group-hover:text-blue-300 transition-colors flex-shrink-0">
                  <span className="text-xs font-medium">
                    {smartNotification.type === 'new_path' ? 'Start' : 'Resume'}
                  </span>
                  <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Other Smart Notifications */}
        {smartNotification && smartNotification.type !== 'continue' && smartNotification.type !== 'new_path' && (
          <SmartNotificationBanner
            notification={smartNotification}
            onDismiss={() => {
              // Mark as dismissed before closing
              if (smartNotification) {
                let notificationKey = `${smartNotification.type}`
                if (smartNotification.data.pathId) {
                  notificationKey += `_${smartNotification.data.pathId}`
                } else if (smartNotification.data.topic) {
                  notificationKey += `_${smartNotification.data.topic}`
                }
                markNotificationDismissed(notificationKey)
              }
              setSmartNotification(null)
            }}
          />
        )}
      </div>
    </div>
  );
}